/**
 * Modelo declarativo da navegação lateral — escopo tenant.
 *
 * Spec: docs/superpowers/specs/2026-06-02-navegacao-ia-lume-atendimento-design.md
 *
 * Política de visibilidade (D3 híbrido):
 *   - Seção é visível ⇔ o usuário tem ≥1 item concedido nela (pertence ao
 *     "mundo" daquela seção). Caso contrário a seção some (gate por papel).
 *   - Dentro de uma seção visível:
 *       item concedido + rota   → 'active' (link)
 *       item concedido + s/rota → 'soon'   (planejado, "em breve")
 *       item barrado  + rota    → 'locked' (upsell por tier; mostra tierLabel)
 *                                 EXCETO `omitWhenBarred` → omitido (P19: recurso
 *                                 de colaboração role-gated, ausente em vez de teaser)
 *       item barrado  + s/rota  → omitido  (não provoca com placeholder fora de alcance)
 *
 * `capability` undefined = item core (sempre concedido).
 */

import {
  BadgeCheck,
  Building2,
  BarChart3,
  Bot,
  Cable,
  CalendarDays,
  ClipboardList,
  Contact,
  CreditCard,
  Eye,
  EyeOff,
  FileText,
  Gauge,
  Gavel,
  Headset,
  Inbox,
  LayoutDashboard,
  ListChecks,
  Megaphone,
  MessageSquareQuote,
  MessagesSquare,
  Scale,
  ScrollText,
  ShieldAlert,
  ShieldCheck,
  Siren,
  Sparkles,
  Timer,
  TriangleAlert,
  UserCog,
  Wrench,
} from 'lucide-react';

import { DOMINIOS } from '../admin/hub/catalog';

import type { DominioId } from '../admin/hub/catalog';
import type { LucideIcon } from 'lucide-react';

export interface NavItem {
  label: string;
  /** Ícone lucide do item (âncora visual de leitura). */
  icon: LucideIcon;
  /** Rota materializada. undefined → item planejado ("em breve"). */
  to?: string;
  /** Capability de backend que concede o item. undefined → core (sempre concedido). */
  capability?: string;
  /** Rótulo de tier exibido quando o item está 'locked' (upsell). Ex.: 'Pro', 'Team+'. */
  tierLabel?: string;
  /**
   * Barrado → OMITIDO inteiramente em vez de exibido como linha 'locked' (upsell).
   * Para recursos de colaboração gateados por papel (P19) que devem estar AUSENTES,
   * não provocados: sem a cap o item simplesmente não existe na navegação.
   * Não confundir com os itens de upsell por tier (Playbooks/Personas/Gatilhos),
   * que permanecem 'locked' para teasing de plano superior.
   */
  omitWhenBarred?: boolean;
  /**
   * NavLink SEM `end`: o item fica ativo também nas rotas filhas. Usado pelos 6 domínios
   * da Administração, cujas ferramentas vivem em `/admin/<dominio>/<id>` (D5, 30/Set/2026).
   */
  matchChildren?: boolean;
}

export interface NavSection {
  id: string;
  title: string;
  items: readonly NavItem[];
}

export type NavItemState = 'active' | 'locked' | 'soon';

export interface ResolvedNavItem extends NavItem {
  state: NavItemState;
}

export interface ResolvedNavSection {
  id: string;
  title: string;
  items: ResolvedNavItem[];
}

/** Ícones dos domínios no menu (prancha 00-B/2a: casa, robô, headset, calendário, elos, cartão). */
const DOMINIO_ICON: Record<DominioId, LucideIcon> = {
  organizacao: Building2,
  ia: Bot,
  atendimento: Headset,
  agenda: CalendarDays,
  integracoes: Cable,
  conta: CreditCard,
};

export const NAV_SECTIONS: readonly NavSection[] = [
  {
    id: 'atendimento',
    title: 'Atendimento',
    items: [
      // Item 364: Painel pessoal é o home do operador (1º item + landing pós-login do
      // atendente). A listagem de conversas foi consolidada na Caixa de entrada (Brief 2B
      // T1) — o item legado "Conversas" (rota '/') foi aposentado (era andaime do protótipo
      // v1, duplicava a Caixa; vide spec navegação §8 e ADR-143).
      { label: 'Painel pessoal', icon: Gauge, to: '/painel', capability: 'agent-dashboard-read' },
      { label: 'Caixa de entrada', icon: Inbox, to: '/inbox' },
      { label: 'Pessoas', icon: Contact, to: '/pessoas', capability: 'person-read' },
      { label: 'Pendências', icon: ListChecks, to: '/pendencias', capability: 'pendencies' },
      { label: 'Agenda', icon: CalendarDays, to: '/agenda', capability: 'appointments' },
      // Brief 2B T6 — Auxiliar: utilidade core (sem gate); cada card respeita a cap da sua fonte.
      { label: 'Auxiliar', icon: Wrench, to: '/auxiliar' },
      // Mensageria interna entre operadores (#8b). Gate P19 em direct-message-participate
      // (Team+, todos os operadores). `omitWhenBarred`: sem a cap o item é OMITIDO
      // (ausente, não vira upsell nem linha desabilitada) — decisão de produto fechada
      // (handoff §1 / briefing §5: Solo = superfície inteiramente ausente).
      {
        label: 'Mensagens',
        icon: MessagesSquare,
        to: '/mensagens',
        capability: 'direct-message-participate',
        omitWhenBarred: true,
      },
    ],
  },
  {
    id: 'supervisao',
    title: 'Supervisão',
    items: [
      {
        label: 'Painel da equipe',
        icon: LayoutDashboard,
        to: '/supervisor',
        capability: 'supervisor-dashboard-read',
      },
      {
        // Item 485 — a tela lista PENDÊNCIAS do time (não conversas); o nome diz o que ela é.
        label: 'Pendências do time',
        icon: ClipboardList,
        to: '/supervisor/inbox',
        capability: 'pendency-team-read',
      },
      {
        label: 'Acompanhamento silencioso',
        icon: Eye,
        to: '/supervisor/acompanhamento',
        capability: 'coattendance-shadow-observer',
        tierLabel: 'Pro',
      },
      {
        label: 'Centro de alertas',
        icon: TriangleAlert,
        to: '/supervisor/alertas',
        capability: 'conflict-alert-metrics-read',
        tierLabel: 'Pro',
      },
      { label: 'Painel de SLA', icon: Timer, to: '/supervisor/sla', capability: 'sla-read' },
      {
        label: 'Dashboard de qualidade',
        icon: BadgeCheck,
        to: '/supervisor/qualidade',
        capability: 'quality-aggregations-read',
        tierLabel: 'Pro',
      },
      // §5 da spec de 17/Set (D5, 30/Set/2026): Qualidade IA é OBSERVAÇÃO (read-only sobre
      // /v1/quality/metrics/agents), não config — sai da seção velha «Configuração da
      // operação» e vem para cá. Mesma cap da Dashboard de qualidade.
      {
        label: 'Qualidade IA',
        icon: Bot,
        to: '/supervisor/qualidade-ia',
        capability: 'quality-aggregations-read',
        tierLabel: 'Pro',
      },
      // Respostas Prontas S4 — métricas de uso por operador + alerta de conduta.
      // Cap opt-in fora de pacote (`canned-response-analytics`); sem a cap o item
      // fica 'locked' (upsell Pro), como as demais telas de supervisão.
      {
        label: 'Respostas Prontas',
        icon: MessageSquareQuote,
        to: '/supervisor/respostas-prontas',
        capability: 'canned-response-analytics',
        tierLabel: 'Pro',
      },
      // F4-b — Campanhas (Raiz de Tarefa): 1 tarefa-mãe → N conversas irmãs.
      // Cap opt-in fora de pacote (`proactive-outbound-campaign`). `omitWhenBarred`
      // (não teaser 'locked'): a fatia aguarda o gate humano de ativação/validação
      // viva do TL (§11.5) — sem a cap granted, o item fica AUSENTE (gated OFF
      // byte-idêntico, nada de upsell prematuro). Ao ativar, o TL pode reclassificar
      // como teaser Pro removendo o omitWhenBarred.
      {
        label: 'Campanhas',
        icon: Megaphone,
        to: '/campanhas',
        capability: 'proactive-outbound-campaign',
        tierLabel: 'Pro',
        omitWhenBarred: true,
      },
      { label: 'Incidentes críticos', icon: Siren, to: '/supervisor/p1', capability: 'p1-read' },
      {
        label: 'Aprovação de overrides',
        icon: ShieldCheck,
        to: '/supervisor/overrides',
        capability: 'override-ai-required-validation',
      },
    ],
  },
  {
    id: 'estrategia',
    title: 'Estratégia',
    items: [
      { label: 'Dashboards estratégicos', icon: BarChart3, capability: 'tenant-config-read' },
      {
        label: 'Insights estratégicos',
        icon: Sparkles,
        to: '/supervisor/insights',
        capability: 'ai-insight-supervisor',
        tierLabel: 'Pro',
      },
      // §5 da spec de 17/Set (D5): a lente GESTOR da mesma tela de insights (Brief 4 Tela 12).
      // A suspeita de duplicação com /supervisor/insights está REGISTRADA na spec e não
      // decidida — mover de seção não é decidir.
      {
        label: 'Insights do gestor',
        icon: Sparkles,
        to: '/estrategia/insights-gestor',
        capability: 'ai-insight-supervisor',
        tierLabel: 'Pro',
      },
    ],
  },
  {
    // D5 (30/Set/2026, prancha 00-B/2a): UMA navegação só. Os 6 domínios sobem para cá,
    // lidos do catálogo — o rail do AdminShell deixou de existir. As FERRAMENTAS não sobem:
    // viram bloco no fim de cada domínio (DomainPage), o que impede este menu de voltar a
    // ser a lista plana de 15 entradas que a seção «Configuração da operação» era.
    // `omitWhenBarred` em Visão geral + domínios: `tenant-config-read` é gate de PAPEL, não
    // de tier — quem não administra não vê 7 linhas trancadas (P19). Usuários e Auditoria
    // mantêm o comportamento de antes.
    // «Visão geral» (não mais «Administração do tenant»): sob o título «Administração» com
    // 9 itens o rótulo curto deixou de ser ambíguo — e é o que a prancha desenha.
    id: 'administracao',
    title: 'Administração',
    items: [
      {
        label: 'Visão geral',
        icon: LayoutDashboard,
        to: '/admin',
        capability: 'tenant-config-read',
        omitWhenBarred: true,
      },
      ...DOMINIOS.map<NavItem>((d) => ({
        label: d.label,
        icon: DOMINIO_ICON[d.id],
        to: d.rota,
        capability: 'tenant-config-read',
        omitWhenBarred: true,
        matchChildren: true,
      })),
      { label: 'Usuários', icon: UserCog, to: '/admin/users', capability: 'users-read' },
      {
        label: 'Explorador de auditoria',
        icon: ScrollText,
        to: '/admin/audit',
        capability: 'audit-events-read',
        tierLabel: 'Team+',
      },
    ],
  },
  {
    id: 'privacidade',
    title: 'Privacidade (LGPD)',
    items: [
      {
        label: 'Centro de direitos do titular',
        icon: Scale,
        to: '/dpo/centro-direitos',
        capability: 'lgpd-requests-read',
      },
      {
        label: 'Centro de conformidade',
        icon: ShieldAlert,
        to: '/dpo/conformidade',
        capability: 'conflict-of-interest-detection',
      },
      {
        label: 'Anonimização',
        icon: EyeOff,
        to: '/dpo/anonimizacao',
        capability: 'lgpd-anonymization-execute',
      },
      {
        label: 'Relatórios LGPD',
        icon: FileText,
        to: '/dpo/relatorios',
        capability: 'lgpd-requests-read',
      },
      {
        label: 'Curadoria / capacidade legal',
        icon: Gavel,
        to: '/dpo/guardianship',
        capability: 'legal-capacity-management',
      },
    ],
  },
];

export function resolveNav(capabilities: readonly string[]): ResolvedNavSection[] {
  const granted = (cap?: string): boolean => cap === undefined || capabilities.includes(cap);
  const out: ResolvedNavSection[] = [];

  for (const section of NAV_SECTIONS) {
    const sectionInhabited = section.items.some((i) => granted(i.capability));
    if (!sectionInhabited) continue; // gate por papel → seção oculta

    const items: ResolvedNavItem[] = [];
    for (const item of section.items) {
      const ok = granted(item.capability);
      if (ok && item.to !== undefined) items.push({ ...item, state: 'active' });
      else if (ok && item.to === undefined) items.push({ ...item, state: 'soon' });
      // barrado + rota: omitido se for recurso role-gated (P19), senão 'locked' (upsell por tier)
      else if (!ok && item.to !== undefined && item.omitWhenBarred !== true)
        items.push({ ...item, state: 'locked' });
      // !ok && item.to === undefined → omitido
      // !ok && item.to !== undefined && omitWhenBarred → omitido (ausente, não locked)
    }
    out.push({ id: section.id, title: section.title, items });
  }

  return out;
}
